import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import "../../partientfeedback.scss";
import SharedHeader from "../../components/SharedHeader";
import JourneyFooter from "../../components/JourneyFooter";
import { useFeedbackForm } from "../../hooks/useFeedbackForm";
import { useJourneyNavigation } from "../../hooks/useJourneyNavigation";
import { hospitalPost } from "../../utils/hospitalApi";

import ScreenOne from "./ScreenOne";
import ScreenTwo from "./ScreenTwo";
import ScreenThree from "./ScreenThree";
import ScreenFour from "./ScreenFour";
import ScreenFive from "./ScreenFive";
import ScreenSix from "./ScreenSix";
import ScreenSeven from "./ScreenSeven";
import ScreenEight from "./ScreenEight";

const PartientFeedback = () => {
  const navigate = useNavigate();
  const { slug: patientId } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get("appointment_id") || "";
  const hospitalUserId = localStorage.getItem("hospital_user_id");
  const [completionOpen, setCompletionOpen] = useState(false);
  const [patient, setPatient] = useState(null);
  const [patientLoading, setPatientLoading] = useState(true);
  const [patientError, setPatientError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const { currentScreen, goToScreen, goBack, goNext } = useJourneyNavigation();
  const {
    feedback,
    updateFeedback,
    updateFeedbackEntry,
    toggleFeedbackOption,
    toggleImprovement,
    resetFeedback,
  } = useFeedbackForm();
  const backgroundImage = `url("/assets/screen${currentScreen}-assets/Slide-0${currentScreen}.webp")`;

  useEffect(() => {
    const pageRoots = [document.documentElement, document.body, document.getElementById("root")].filter(Boolean);
    pageRoots.forEach((element) => element.classList.add("feedback-journey-active"));
    return () => pageRoots.forEach((element) => element.classList.remove("feedback-journey-active"));
  }, []);

  useEffect(() => {
    if (!hospitalUserId) {
      navigate("/");
      return;
    }
    let active = true;
    async function loadPatient() {
      setPatientLoading(true);
      setPatientError("");
      const response = await hospitalPost("hospital-get-feedback-patient", {
        hospital_user_id: hospitalUserId,
        patient_id: patientId,
        hospital_appointment_id: appointmentId,
      });
      if (!active) return;
      setPatientLoading(false);
      if (response.data.status === 1) setPatient(response.data.patient);
      else setPatientError(response.data.msg || "Unable to load patient details.");
    }
    loadPatient();
    return () => { active = false; };
  }, [appointmentId, hospitalUserId, navigate, patientId]);

  const submitFeedback = useCallback(async () => {
    if (!patient || submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setSubmitError("");
    const response = await hospitalPost("hospital-save-patient-feedback", {
      hospital_user_id: hospitalUserId,
      patient_id: patient.patient_id,
      hospital_appointment_id: patient.hospital_appointment_id,
      mood: feedback.mood,
      therapy_rating: feedback.therapyRating,
      factors_json: JSON.stringify(feedback.factors),
      other_factor: feedback.otherFactor,
      stress_before: feedback.stressBefore,
      stress_after: feedback.stressAfter,
      improvements_json: JSON.stringify(feedback.improvements),
      improvement_other: feedback.improvementOther,
      experience_json: JSON.stringify(feedback.experience),
      comment: feedback.comment,
    });
    setSubmitting(false);
    submitLockRef.current = false;
    if (response.data.status === 1) {
      setCompletionOpen(true);
      setPatient((current) => ({ ...current, feedback_count: Number(current.feedback_count || 0) + 1 }));
    } else {
      setSubmitError(response.data.msg || "Unable to submit feedback. Please try again.");
    }
  }, [feedback, hospitalUserId, patient]);
  function openScreen(screenNumber) {
    setCompletionOpen(false);
    goToScreen(screenNumber);
  }

  function goToPreviousScreen() {
    setCompletionOpen(false);
    goBack();
  }
  function resetJourney() {
    resetFeedback();
    setCompletionOpen(false);
    goToScreen(1);
  }
  function handleNextOrSubmit() {
    if (currentScreen === 8) {
      submitFeedback();
      return;
    }

    goNext();
  }

  if (patientLoading) {
    return <div className="feedback-page-state"><div className="feedback-page-state__loader" /><strong>Loading patient details...</strong></div>;
  }
  if (patientError || !patient) {
    return <div className="feedback-page-state"><strong>{patientError || "Patient details are unavailable."}</strong><button type="button" onClick={() => navigate("/appointments")}>Back to Patient List</button></div>;
  }

  return (
    <div
      className={`journey-app screen-${currentScreen}`}
      style={{ "--screen-background": backgroundImage }}
    >
      <div className="journey-background" aria-hidden="true" />
      <SharedHeader
        currentScreen={currentScreen}
        onScreenChange={openScreen}
        onReset={resetJourney}
      />

      {currentScreen !== 1 && (
        <div className="feedback-patient-chip" aria-label="Selected patient">
          <strong>{patient.customer_name}</strong>
          <span>{patient.patient_id} | {patient.phone_number}</span>
        </div>
      )}

      <div className="screen-pill">SCREEN {currentScreen} OF 8</div>

      <main
        className={`journey-content${currentScreen === 1 ? " journey-content--screen-one" : ""}${currentScreen === 2 ? " journey-content--screen-two" : ""}${currentScreen === 3 ? " journey-content--screen-three" : ""}${currentScreen === 4 ? " journey-content--screen-four" : ""}${currentScreen === 5 ? " journey-content--screen-five" : ""}${currentScreen === 6 ? " journey-content--screen-six" : ""}${currentScreen === 7 ? " journey-content--screen-seven" : ""}${currentScreen === 8 ? " journey-content--screen-eight" : ""}`}
        id="journey-content"
      >
        {currentScreen === 1 ? (
          <ScreenOne patient={patient} />
        ) : currentScreen === 2 ? (
          <ScreenTwo
            selectedMood={feedback.mood}
            onMoodChange={(mood) => updateFeedback("mood", mood)}
            onContinue={goNext}
          />
        ) : currentScreen === 3 ? (
          <ScreenThree
            rating={feedback.therapyRating}
            selectedFactors={feedback.factors}
            otherFactor={feedback.otherFactor}
            onRatingChange={(rating) => updateFeedback("therapyRating", rating)}
            onFactorToggle={(factor) => toggleFeedbackOption("factors", factor)}
            onOtherFactorChange={(value) =>
              updateFeedback("otherFactor", value)
            }
          />
        ) : currentScreen === 4 ? (
          <ScreenFour
            stressBefore={feedback.stressBefore}
            stressAfter={feedback.stressAfter}
            onStressChange={updateFeedback}
          />
        ) : currentScreen === 5 ? (
          <ScreenFive
            selectedImprovements={feedback.improvements}
            improvementOther={feedback.improvementOther}
            onImprovementToggle={toggleImprovement}
            onImprovementOtherChange={(value) =>
              updateFeedback("improvementOther", value)
            }
          />
        ) : currentScreen === 6 ? (
          <ScreenSix
            experience={feedback.experience}
            onExperienceChange={(question, rating) =>
              updateFeedbackEntry("experience", question, rating)
            }
          />
        ) : currentScreen === 7 ? (
          <ScreenSeven />
        ) : currentScreen === 8 ? (
          <ScreenEight
            feedback={feedback}
            patient={patient}
            onCommentChange={(comment) => updateFeedback("comment", comment)}
            completionOpen={completionOpen}
            onSubmit={submitFeedback}
            onCloseCompletion={() => setCompletionOpen(false)}
            submitting={submitting}
            submitError={submitError}
          />
        ) : null}
      </main>

      <JourneyFooter
        currentScreen={currentScreen}
        onBack={goToPreviousScreen}
        onNext={handleNextOrSubmit}
      />
    </div>
  );
}

export default PartientFeedback;
